import {
  type GetOrganisationFeaturesError,
  useGetOrganisationFeatures,
  usePatchOrganisationFeatures,
} from '@/generated/http-clients/v3ApiComponents'
import type { OrganisationFeatures } from '@/generated/http-clients/v3ApiSchemas'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import {
  ERROR_TOAST_OPTION,
  LOADING_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
  toaster,
} from '@/hooks/useToast/toastUtils'
import type { UseQueryResult } from '@tanstack/react-query'
import get from 'lodash/get'
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'

type OrganizationContextType = {
  selectedOrganizationId: string
  setSelectedOrganizationId: (organizationId: string) => void
  clearSelectedOrganizationId: () => void
  orgFeatures: UseQueryResult<
    OrganisationFeatures,
    GetOrganisationFeaturesError
  >
  patchOrganisationFeatures: (
    orgFeatures: OrganisationFeatures,
  ) => Promise<OrganisationFeatures>
}

const OrganizationContext = createContext<OrganizationContextType>(
  {} as OrganizationContextType,
)

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider',
    )
  }
  return context
}

const ACTIVE_ORG_ID_KEY = 'hivemq-cloud-backoffice:activeOrganizationId'

export function OrganizationProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const { t } = useTranslation('features')

  const [localData, setLocalData] = useLocalStorage(ACTIVE_ORG_ID_KEY, {
    selectedOrganizationId: '',
  })

  const hasValidOrgId = Boolean(localData.selectedOrganizationId)

  const orgFeatures = useGetOrganisationFeatures(
    {
      pathParams: {
        orgId: localData.selectedOrganizationId,
      },
    },
    {
      enabled: hasValidOrgId,
      retry: hasValidOrgId,
    },
  )

  const patchOrganisationFeaturesRequest = usePatchOrganisationFeatures({
    onMutate: () => {
      const toastId = 'org-features-patch-toast'
      toaster.create({
        ...LOADING_TOAST_OPTION,
        id: toastId,
        title: t('organization.features.patchOrgFeatures.loadingTitle'),
        description: t(
          'organization.features.patchOrgFeatures.loadingDescription',
        ),
      })
      return toastId
    },
    onSuccess: (_data: unknown, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...SUCCESS_TOAST_OPTION,
        title: t('organization.features.patchOrgFeatures.successTitle'),
        description: t(
          'organization.features.patchOrgFeatures.successDescription',
        ),
      }
      showOrUpdateToast(String(toastId), toastContent)
      orgFeatures.refetch()
    },
    onError: (error, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...ERROR_TOAST_OPTION,
        title: t('organization.features.patchOrgFeatures.errorTitle'),
        description: String(
          get(error, 'stack.description') ||
            get(error, 'stack._embedded.errors[0].message') ||
            get(error, 'message') ||
            t('organization.features.patchOrgFeatures.errorDescription'),
        ),
      }
      showOrUpdateToast(String(toastId), toastContent)
    },
  })

  const patchOrganisationFeatures = useCallback(
    (orgFeatures: OrganisationFeatures) => {
      if (!hasValidOrgId) {
        return Promise.reject(new Error('No valid organization ID selected'))
      }

      return patchOrganisationFeaturesRequest.mutateAsync({
        pathParams: {
          orgId: localData.selectedOrganizationId,
        },
        body: orgFeatures,
      })
    },
    [
      hasValidOrgId,
      localData.selectedOrganizationId,
      patchOrganisationFeaturesRequest,
    ],
  )

  const setSelectedOrganizationId = useCallback(
    (newId: string) => {
      setLocalData({ selectedOrganizationId: newId })
    },
    [setLocalData],
  )

  const clearSelectedOrganizationId = useCallback(() => {
    setLocalData({ selectedOrganizationId: '' })
  }, [setLocalData])

  const contextValue = useMemo(
    () => ({
      selectedOrganizationId: localData.selectedOrganizationId,
      setSelectedOrganizationId,
      clearSelectedOrganizationId,
      orgFeatures,
      patchOrganisationFeatures,
    }),
    [
      localData.selectedOrganizationId,
      setSelectedOrganizationId,
      clearSelectedOrganizationId,
      orgFeatures,
      patchOrganisationFeatures,
    ],
  )

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  )
}
